import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Locale } from '../i18n/types';
import type { ChecklistCategory, Priority } from '../types';
import { buildSnapshot, formatDateInput, type PregnancySnapshot } from '../lib/pregnancy';
import {
  createCustomId,
  emptyCustomizations,
  itemKey,
  mergeCustomizations,
  type ItemCustomizations,
} from '../lib/customizations';
import {
  buildBackupPayload,
  emptyAccount,
  getActiveAccount,
  loadStore,
  removeAccount,
  saveStore,
  upsertAccount,
  type BackupPayload,
  type UserAccount,
  type UserStore,
} from '../lib/userStore';

function suggestedDueDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 140);
  return formatDateInput(d);
}

export interface UseUserDataResult {
  store: UserStore;
  account: UserAccount | null;
  snapshot: PregnancySnapshot;
  suggestedDueDate: string;
  accounts: UserAccount[];
  needsOnboarding: boolean;
  customizations: ItemCustomizations;
  setLocaleForActive: (locale: Locale) => void;
  createOrUpdateFromOnboarding: (input: {
    name: string;
    locale: Locale;
    dueDate?: string | null;
    birthDate?: string | null;
    skipDates?: boolean;
  }) => void;
  updateActiveDates: (input: { dueDate?: string | null; birthDate?: string | null }) => void;
  clearActiveDates: () => void;
  renameActive: (name: string) => void;
  switchAccount: (id: string) => void;
  addBlankAccount: () => void;
  deleteAccount: (id: string) => void;
  toggleChecked: (stageId: string, itemId: string) => void;
  isChecked: (stageId: string, itemId: string) => boolean;
  importChecked: (keys: string[]) => void;
  replaceChecked: (keys: string[]) => void;
  resetStage: (stageId: string) => void;
  resetAllChecked: () => void;
  stageCheckedCount: (stageId: string) => number;
  stageProgress: (stageId: string, itemCount: number) => number;
  overallProgress: (totalCount: number) => number;
  checkedCount: number;
  exportActiveBackup: () => BackupPayload | null;
  applyBackup: (payload: BackupPayload) => boolean;
  applyFamilyShare: (payload: BackupPayload) => { ok: boolean };
  hideChecklistItem: (stageId: string, itemId: string) => void;
  restoreChecklistItem: (stageId: string, itemId: string) => void;
  renameChecklistItem: (stageId: string, itemId: string, text: string, isCustom: boolean) => void;
  addChecklistItem: (input: {
    stageId: string;
    text: string;
    category: ChecklistCategory;
    note?: string;
  }) => void;
  removeCustomChecklistItem: (stageId: string, itemId: string) => void;
  hideShoppingItem: (stageId: string, itemId: string) => void;
  renameShoppingItem: (stageId: string, itemId: string, name: string, isCustom: boolean) => void;
  addShoppingItem: (input: {
    stageId: string;
    name: string;
    priority?: Priority;
    timing?: string;
    note?: string;
  }) => void;
  removeCustomShoppingItem: (stageId: string, itemId: string) => void;
  restoreStageDefaults: (stageId: string) => void;
}

export function useUserData(uiLocaleFallback: Locale = 'ja'): UseUserDataResult {
  const [store, setStore] = useState<UserStore>(() => loadStore());

  useEffect(() => {
    saveStore(store);
  }, [store]);

  const account = useMemo(() => getActiveAccount(store), [store]);

  const snapshot = useMemo(
    () =>
      buildSnapshot({
        dueDate: account?.dueDate ?? null,
        birthDate: account?.birthDate ?? null,
        locale: account?.locale ?? uiLocaleFallback,
      }),
    [account?.dueDate, account?.birthDate, account?.locale, uiLocaleFallback]
  );

  const needsOnboarding = !account || (!account.onboarded && !account.dueDate && !account.birthDate);

  const patchActive = useCallback((fn: (prev: UserAccount) => UserAccount) => {
    setStore((prev) => {
      const current = getActiveAccount(prev);
      if (!current) return prev;
      return upsertAccount(prev, fn(current));
    });
  }, []);

  const setLocaleForActive = useCallback(
    (locale: Locale) => {
      setStore((prev) => {
        const current = getActiveAccount(prev);
        if (!current) {
          const created = emptyAccount({ locale, onboarded: false });
          return upsertAccount(prev, created);
        }
        return upsertAccount(prev, { ...current, locale });
      });
    },
    []
  );

  const createOrUpdateFromOnboarding = useCallback(
    (input: {
      name: string;
      locale: Locale;
      dueDate?: string | null;
      birthDate?: string | null;
      skipDates?: boolean;
    }) => {
      setStore((prev) => {
        const current = getActiveAccount(prev);
        const base = current ?? emptyAccount({ locale: input.locale });
        const next = emptyAccount({
          ...base,
          name: input.name.trim() || base.name,
          locale: input.locale,
          dueDate: input.skipDates ? null : input.dueDate ?? null,
          birthDate: input.skipDates ? null : input.birthDate ?? null,
          onboarded: true,
          checked: base.checked,
        });
        if (next.dueDate) next.birthDate = null;
        if (next.birthDate) next.dueDate = null;
        return upsertAccount(prev, next);
      });
    },
    []
  );

  const updateActiveDates = useCallback(
    (input: { dueDate?: string | null; birthDate?: string | null }) => {
      patchActive((prev) => {
        const next = { ...prev, onboarded: true };
        if (input.dueDate !== undefined) {
          next.dueDate = input.dueDate;
          if (input.dueDate) next.birthDate = null;
        }
        if (input.birthDate !== undefined) {
          next.birthDate = input.birthDate;
          if (input.birthDate) next.dueDate = null;
        }
        return next;
      });
    },
    [patchActive]
  );

  const clearActiveDates = useCallback(() => {
    patchActive((prev) => ({ ...prev, dueDate: null, birthDate: null }));
  }, [patchActive]);

  const renameActive = useCallback(
    (name: string) => {
      patchActive((prev) => ({ ...prev, name: name.trim() || prev.name }));
    },
    [patchActive]
  );

  const switchAccount = useCallback((id: string) => {
    setStore((prev) => {
      if (!prev.accounts.some((a) => a.id === id)) return prev;
      return { ...prev, activeId: id };
    });
  }, []);

  const addBlankAccount = useCallback(() => {
    setStore((prev) => {
      const created = emptyAccount({
        name: `ユーザー${prev.accounts.length + 1}`,
        locale: getActiveAccount(prev)?.locale ?? 'ja',
        onboarded: false,
      });
      return upsertAccount(prev, created);
    });
  }, []);

  const deleteAccount = useCallback((id: string) => {
    setStore((prev) => removeAccount(prev, id));
  }, []);

  const toggleChecked = useCallback(
    (stageId: string, itemId: string) => {
      const key = `${stageId}:${itemId}`;
      patchActive((prev) => {
        const set = new Set(prev.checked);
        if (set.has(key)) set.delete(key);
        else set.add(key);
        return { ...prev, checked: Array.from(set) };
      });
    },
    [patchActive]
  );

  const isChecked = useCallback(
    (stageId: string, itemId: string) => {
      if (!account) return false;
      return account.checked.includes(`${stageId}:${itemId}`);
    },
    [account]
  );

  const importChecked = useCallback(
    (keys: string[]) => {
      patchActive((prev) => {
        const set = new Set(prev.checked);
        for (const key of keys) {
          if (typeof key === 'string' && key.includes(':')) set.add(key);
        }
        return { ...prev, checked: Array.from(set) };
      });
    },
    [patchActive]
  );

  const replaceChecked = useCallback(
    (keys: string[]) => {
      patchActive((prev) => ({
        ...prev,
        checked: keys.filter((k) => typeof k === 'string' && k.includes(':')),
      }));
    },
    [patchActive]
  );

  const resetStage = useCallback(
    (stageId: string) => {
      const prefix = `${stageId}:`;
      patchActive((prev) => ({
        ...prev,
        checked: prev.checked.filter((k) => !k.startsWith(prefix)),
      }));
    },
    [patchActive]
  );

  const resetAllChecked = useCallback(() => {
    if (typeof window !== 'undefined' && !window.confirm('すべてのチェックをリセットしますか？')) {
      return;
    }
    patchActive((prev) => ({ ...prev, checked: [] }));
  }, [patchActive]);

  const stageCheckedCount = useCallback(
    (stageId: string) => {
      if (!account) return 0;
      const prefix = `${stageId}:`;
      return account.checked.filter((k) => k.startsWith(prefix)).length;
    },
    [account]
  );

  const stageProgress = useCallback(
    (stageId: string, itemCount: number) => {
      if (itemCount === 0) return 0;
      return stageCheckedCount(stageId) / itemCount;
    },
    [stageCheckedCount]
  );

  const overallProgress = useCallback(
    (totalCount: number) => {
      if (!account || totalCount === 0) return 0;
      return account.checked.length / totalCount;
    },
    [account]
  );

  const exportActiveBackup = useCallback(() => {
    if (!account) return null;
    return buildBackupPayload(account);
  }, [account]);

  const applyBackup = useCallback((payload: BackupPayload) => {
    if (!payload || payload.version !== 2 || !payload.account) return false;
    setStore((prev) => upsertAccount(prev, emptyAccount(payload.account)));
    return true;
  }, []);

  /** 家族共有: チェック・予定日・カスタム項目をマージ（自分の名前は維持） */
  const applyFamilyShare = useCallback((payload: BackupPayload) => {
    if (!payload || payload.version !== 2 || !payload.account) {
      return { ok: false as const };
    }
    const incoming = emptyAccount(payload.account);
    setStore((prev) => {
      const current = getActiveAccount(prev);
      if (!current) {
        return upsertAccount(
          prev,
          emptyAccount({
            ...incoming,
            name: incoming.name || '家族',
            onboarded: true,
          })
        );
      }
      const mergedChecked = Array.from(
        new Set([...(current.checked || []), ...(incoming.checked || [])])
      );
      return upsertAccount(
        prev,
        emptyAccount({
          ...current,
          dueDate: incoming.dueDate ?? current.dueDate,
          birthDate: incoming.birthDate ?? current.birthDate,
          checked: mergedChecked,
          customizations: mergeCustomizations(current.customizations, incoming.customizations),
          locale: current.locale,
          onboarded: true,
        })
      );
    });
    return { ok: true as const };
  }, []);

  const patchCustomizations = useCallback(
    (fn: (prev: ItemCustomizations) => ItemCustomizations) => {
      patchActive((prev) => ({
        ...prev,
        customizations: emptyCustomizations(fn(emptyCustomizations(prev.customizations))),
      }));
    },
    [patchActive]
  );

  const hideChecklistItem = useCallback(
    (stageId: string, itemId: string) => {
      const key = itemKey(stageId, itemId);
      patchCustomizations((c) => ({
        ...c,
        hiddenChecklist: Array.from(new Set([...c.hiddenChecklist, key])),
      }));
      // チェック済みも外す
      patchActive((prev) => ({
        ...prev,
        checked: prev.checked.filter((k) => k !== key),
      }));
    },
    [patchCustomizations, patchActive]
  );

  const restoreChecklistItem = useCallback(
    (stageId: string, itemId: string) => {
      const key = itemKey(stageId, itemId);
      patchCustomizations((c) => ({
        ...c,
        hiddenChecklist: c.hiddenChecklist.filter((k) => k !== key),
      }));
    },
    [patchCustomizations]
  );

  const renameChecklistItem = useCallback(
    (stageId: string, itemId: string, text: string, isCustom: boolean) => {
      const next = text.trim();
      if (!next) return;
      if (isCustom) {
        patchCustomizations((c) => ({
          ...c,
          customChecklist: c.customChecklist.map((item) =>
            item.stageId === stageId && item.id === itemId ? { ...item, text: next } : item
          ),
        }));
        return;
      }
      const key = itemKey(stageId, itemId);
      patchCustomizations((c) => ({
        ...c,
        checklistText: { ...c.checklistText, [key]: next },
      }));
    },
    [patchCustomizations]
  );

  const addChecklistItem = useCallback(
    (input: { stageId: string; text: string; category: ChecklistCategory; note?: string }) => {
      const text = input.text.trim();
      if (!text) return;
      patchCustomizations((c) => ({
        ...c,
        customChecklist: [
          ...c.customChecklist,
          {
            stageId: input.stageId,
            id: createCustomId('c'),
            text,
            category: input.category,
            note: input.note?.trim() || undefined,
          },
        ],
      }));
    },
    [patchCustomizations]
  );

  const removeCustomChecklistItem = useCallback(
    (stageId: string, itemId: string) => {
      const key = itemKey(stageId, itemId);
      patchCustomizations((c) => ({
        ...c,
        customChecklist: c.customChecklist.filter(
          (item) => !(item.stageId === stageId && item.id === itemId)
        ),
      }));
      patchActive((prev) => ({
        ...prev,
        checked: prev.checked.filter((k) => k !== key),
      }));
    },
    [patchCustomizations, patchActive]
  );

  const hideShoppingItem = useCallback(
    (stageId: string, itemId: string) => {
      const key = itemKey(stageId, itemId);
      patchCustomizations((c) => ({
        ...c,
        hiddenShopping: Array.from(new Set([...c.hiddenShopping, key])),
      }));
    },
    [patchCustomizations]
  );

  const renameShoppingItem = useCallback(
    (stageId: string, itemId: string, name: string, isCustom: boolean) => {
      const next = name.trim();
      if (!next) return;
      if (isCustom) {
        patchCustomizations((c) => ({
          ...c,
          customShopping: c.customShopping.map((item) =>
            item.stageId === stageId && item.id === itemId ? { ...item, name: next } : item
          ),
        }));
        return;
      }
      const key = itemKey(stageId, itemId);
      patchCustomizations((c) => ({
        ...c,
        shoppingName: { ...c.shoppingName, [key]: next },
      }));
    },
    [patchCustomizations]
  );

  const addShoppingItem = useCallback(
    (input: {
      stageId: string;
      name: string;
      priority?: Priority;
      timing?: string;
      note?: string;
    }) => {
      const name = input.name.trim();
      if (!name) return;
      patchCustomizations((c) => ({
        ...c,
        customShopping: [
          ...c.customShopping,
          {
            stageId: input.stageId,
            id: createCustomId('s'),
            name,
            priority: input.priority ?? 'should',
            timing: input.timing?.trim() || 'いつでも',
            priceYenMin: 0,
            priceYenMax: 0,
            note: input.note?.trim() || undefined,
          },
        ],
      }));
    },
    [patchCustomizations]
  );

  const removeCustomShoppingItem = useCallback(
    (stageId: string, itemId: string) => {
      patchCustomizations((c) => ({
        ...c,
        customShopping: c.customShopping.filter(
          (item) => !(item.stageId === stageId && item.id === itemId)
        ),
      }));
    },
    [patchCustomizations]
  );

  const restoreStageDefaults = useCallback(
    (stageId: string) => {
      const prefix = `${stageId}:`;
      patchCustomizations((c) => ({
        ...c,
        hiddenChecklist: c.hiddenChecklist.filter((k) => !k.startsWith(prefix)),
        hiddenShopping: c.hiddenShopping.filter((k) => !k.startsWith(prefix)),
        checklistText: Object.fromEntries(
          Object.entries(c.checklistText).filter(([k]) => !k.startsWith(prefix))
        ),
        shoppingName: Object.fromEntries(
          Object.entries(c.shoppingName).filter(([k]) => !k.startsWith(prefix))
        ),
        customChecklist: c.customChecklist.filter((item) => item.stageId !== stageId),
        customShopping: c.customShopping.filter((item) => item.stageId !== stageId),
      }));
    },
    [patchCustomizations]
  );

  return {
    store,
    account,
    snapshot,
    suggestedDueDate: suggestedDueDate(),
    accounts: store.accounts,
    needsOnboarding,
    customizations: emptyCustomizations(account?.customizations),
    setLocaleForActive,
    createOrUpdateFromOnboarding,
    updateActiveDates,
    clearActiveDates,
    renameActive,
    switchAccount,
    addBlankAccount,
    deleteAccount,
    toggleChecked,
    isChecked,
    importChecked,
    replaceChecked,
    resetStage,
    resetAllChecked,
    stageCheckedCount,
    stageProgress,
    overallProgress,
    checkedCount: account?.checked.length ?? 0,
    exportActiveBackup,
    applyBackup,
    applyFamilyShare,
    hideChecklistItem,
    restoreChecklistItem,
    renameChecklistItem,
    addChecklistItem,
    removeCustomChecklistItem,
    hideShoppingItem,
    renameShoppingItem,
    addShoppingItem,
    removeCustomShoppingItem,
    restoreStageDefaults,
  };
}
